import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { useLanguage } from '../../contexts/LanguageContext';

const IconKey = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);
const IconClose = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const IconCheck = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
  </svg>
);

const ForgotPasswordModal = ({ show, onClose }) => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setMessage(response.data.message);
      setEmail(''); // Clear form
    } catch (error) {
      setError(error.response?.data?.detail || t('forgotPassword.sendError', 'Error al enviar el enlace'));
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fp-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="fp-modal">
        <button onClick={onClose} className="fp-close" aria-label={t('forgotPassword.close', 'Cerrar')}>
          <IconClose/>
        </button>

        <div className="fp-icon-wrap">
          <IconKey/>
        </div>

        <h3 className="fp-title">{t('forgotPassword.title', 'Recuperar contraseña')}</h3>

        {message ? (
          <div className="fp-success">
            <div className="fp-success-icon"><IconCheck/></div>
            <p className="fp-success-text">{message}</p>
            <button onClick={onClose} className="fp-btn fp-btn-primary" style={{ width: '100%' }}>
              {t('forgotPassword.understood', 'Entendido')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="fp-form">
            <p className="fp-body">
              {t('forgotPassword.body', 'Te enviaremos un enlace para restablecer tu contraseña.')}
            </p>

            {error && <div className="fp-error" role="alert">{error}</div>}

            <div className="fp-field">
              <label htmlFor="forgot-email" className="fp-sr-only">Email</label>
              <input
                id="forgot-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@empresa.com"
                required
                disabled={loading}
                className="fp-input"
              />
            </div>

            <div className="fp-actions">
              <button type="button" onClick={onClose} className="fp-btn fp-btn-ghost">
                {t('forgotPassword.cancel', 'Cancelar')}
              </button>
              <button type="submit" disabled={loading || !email} className="fp-btn fp-btn-primary">
                {loading ? <span className="fp-spinner" aria-label={t('forgotPassword.loading', 'Cargando')}/> : t('forgotPassword.sendLink', 'Enviar enlace')}
              </button>
            </div>
          </form>
        )}
      </div>

      <style>{CSS}</style>
    </div>
  );
};

const CSS = `
.fp-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.6);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;z-index:60;padding:20px}
.fp-modal{position:relative;width:100%;max-width:400px;background:#121212;border:1px solid #222;border-radius:20px;padding:36px 32px 32px;box-shadow:0 24px 60px rgba(0,0,0,.6);font-family:'Inter',system-ui,sans-serif;text-align:center;animation:fpModalIn .28s cubic-bezier(.22,1,.36,1) both}
@keyframes fpModalIn{from{opacity:0;transform:scale(.96) translateY(10px)}to{opacity:1;transform:scale(1) translateY(0)}}

.fp-close{position:absolute;top:16px;right:16px;width:32px;height:32px;display:flex;align-items:center;justify-content:center;border-radius:8px;background:none;border:none;color:#666;cursor:pointer;transition:color .15s,background .15s}
.fp-close:hover{color:#fff;background:rgba(255,255,255,.06)}

.fp-icon-wrap{width:52px;height:52px;margin:0 auto 20px;border-radius:50%;background:rgba(0,113,227,.14);color:#0071e3;display:flex;align-items:center;justify-content:center}

.fp-title{font-size:19px;font-weight:700;letter-spacing:-.01em;color:#fff;margin-bottom:6px}
.fp-body{font-size:13.5px;color:#9a9a9a;line-height:1.5;margin-bottom:18px}

.fp-form{display:flex;flex-direction:column;gap:14px}
.fp-error{background:rgba(220,38,38,.12);border:1px solid rgba(220,38,38,.35);color:#ff6b6b;padding:10px 14px;border-radius:8px;font-size:12.5px;line-height:1.4;text-align:left}

.fp-field{text-align:left}
.fp-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
.fp-input{width:100%;height:46px;padding:0 14px;border:1.5px solid #262626;border-radius:10px;font-family:'Inter',system-ui,sans-serif;font-size:14.5px;color:#fff;background:#000;outline:none;transition:border-color .15s,box-shadow .15s;box-sizing:border-box}
.fp-input::placeholder{color:#666}
.fp-input:focus{border-color:#0071e3;box-shadow:0 0 0 3px rgba(0,113,227,.18)}
.fp-input:disabled{opacity:.5}

.fp-actions{display:flex;gap:10px;margin-top:2px}
.fp-btn{flex:1;height:46px;border-radius:100px;font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;transition:opacity .15s,background .15s,border-color .15s,transform .15s}
.fp-btn:active{transform:scale(.97)}
.fp-btn-primary{background:#ededed;color:#0a0a0a;border:none}
.fp-btn-primary:hover{opacity:.87}
.fp-btn-primary:disabled{opacity:.35;cursor:not-allowed}
.fp-btn-ghost{background:transparent;color:#fff;border:1.5px solid #262626}
.fp-btn-ghost:hover{background:rgba(255,255,255,.06);border-color:#3a3a3a}

.fp-success{display:flex;flex-direction:column;gap:14px}
.fp-success-icon{width:44px;height:44px;margin:0 auto;border-radius:50%;background:rgba(22,163,74,.14);color:#16a34a;display:flex;align-items:center;justify-content:center}
.fp-success-text{font-size:13.5px;color:#c8c8c8;line-height:1.5}

@keyframes fpSpin{to{transform:rotate(360deg)}}
.fp-spinner{width:15px;height:15px;border:2px solid rgba(0,0,0,.2);border-top-color:#0a0a0a;border-radius:50%;animation:fpSpin .55s linear infinite}

@media(max-width:480px){
  .fp-modal{padding:28px 22px 24px;border-radius:16px}
  .fp-title{font-size:17px}
}
`;

export default ForgotPasswordModal;
